import {
  DisplayService,
  type FormService,
  type Group,
  type IDisplayService,
  type Question,
} from '@stepform';

export class MentalHealthDisplayService extends DisplayService implements IDisplayService {
  constructor(formService: FormService) {
    super(formService);
  }

  public showQuestion(question: Question, group: Group): boolean {
    switch (question.id) {
      case 'WhoIsAffected':
        return this.showWhoIsAffected();
      case 'PermissionToRaise':
        return this.showPermissionToRaise();
      case 'SelfStillEmployed':
        return this.showSelfStillEmployed();
      case 'DescribeConcern':
        return this.showDescribeConcern();
      case 'RaisedWithAnotherOrganisationOther':
        return this.showRaisedWithAnotherOrganisationOther();
      case 'OtherOrganisationActions':
        return this.showOtherOrganisationActions();
      default:
        return true;
    }
  }

  public showGroup(group: Group): boolean {
    switch (group.id) {
      case 'group-persons-affected':
        return this.showGroupPersonsAffected();
      case 'group-specific-person':
        return this.showGroupSpecificPerson();
      default:
        return true;
    }
  }

  public showCallout(id: string): boolean {
    switch (id) {
      case 'callout-work-associated':
        return this.showCalloutWorkAssociated();
      case 'callout-permission-to-raise':
        return this.showCalloutPermissionToRaise();
      case 'callout-consent-contact':
        return this.showCalloutConsentContact();
      case 'callout-consent-inform-business':
        return this.showCalloutConsentInformBusiness();
      default:
        return true;
    }
  }

  private getBooleanAnswer(questionId: string): boolean {
    const question = this.formService.getQuestionById(questionId);
    return question ? question.answer === '1' : false;
  }

  private showGroupSpecificPerson(): boolean {
    return this.getBooleanAnswer('ConcernSpecificPerson');
  }

  private showWhoIsAffected(): boolean {
    const occurredAtWork = this.formService.getQuestionById('OccurredAtWork');
    return occurredAtWork?.hasPopulatedAnswer() ?? false;
  }

  private showPermissionToRaise(): boolean {
    const whoIsAffected = this.formService.getQuestionById('WhoIsAffected');
    return (
      this.showWhoIsAffected() &&
      ['someone_else', 'group_of_people', 'connected_workplace'].includes(
        String(whoIsAffected?.answer ?? '')
      )
    );
  }

  private showSelfStillEmployed(): boolean {
    const whoIsAffected = this.formService.getQuestionById('WhoIsAffected');
    return ['broader_issues'].includes(String(whoIsAffected?.answer ?? ''));
  }

  private showGroupPersonsAffected(): boolean {
    const permissionToRaise = this.formService.getQuestionById('PermissionToRaise');
    return (
      permissionToRaise?.hasPopulatedAnswer() &&
      permissionToRaise.answer !== 'no' &&
      this.showPermissionToRaise()
    );
  }

  private showDescribeConcern(): boolean {
    const whoIsAffected = this.formService.getQuestionById('WhoIsAffected');
    return (
      ['me', 'broader_issues'].includes(String(whoIsAffected?.answer ?? '')) ||
      this.showGroupPersonsAffected()
    );
  }

  private showRaisedWithAnotherOrganisationOther(): boolean {
    const raisedWithAnotherOrganisation = this.formService.getQuestionById(
      'RaisedWithAnotherOrganisation'
    );
    return (
      raisedWithAnotherOrganisation?.hasPopulatedAnswer() &&
      String(raisedWithAnotherOrganisation.answer).includes('other')
    );
  }

  private showOtherOrganisationActions(): boolean {
    const raisedWithAnotherOrganisation = this.formService.getQuestionById(
      'RaisedWithAnotherOrganisation'
    );
    const possibleAnswers = [
      'police',
      'employment_relations_authority',
      'mbie_mediation_service',
      'human_rights_commission',
      'union_representative',
      'employment_advocate',
      'lawyer',
      'other',
    ];
    const selectedAnswers = String(raisedWithAnotherOrganisation?.answer ?? '').split(', ');
    return (
      raisedWithAnotherOrganisation?.hasPopulatedAnswer() &&
      selectedAnswers.some((entity) => possibleAnswers.includes(entity))
    );
  }

  private showCalloutWorkAssociated(): boolean {
    const occurredAtWork = this.formService.getQuestionById('OccurredAtWork');
    return occurredAtWork?.hasAnswered() && occurredAtWork.answer === 'no';
  }

  private showCalloutPermissionToRaise(): boolean {
    const permissionToRaise = this.formService.getQuestionById('PermissionToRaise');
    return (
      permissionToRaise?.hasAnswered() &&
      permissionToRaise.answer === 'no' &&
      this.showPermissionToRaise()
    );
  }

  private showCalloutConsentContact(): boolean {
    const consentContact = this.formService.getQuestionById('ConsentContact');
    return consentContact?.hasAnswered() && !this.getBooleanAnswer('ConsentContact');
  }

  private showCalloutConsentInformBusiness(): boolean {
    const consentInformBusiness = this.formService.getQuestionById('ConsentInformBusiness');
    return consentInformBusiness?.hasAnswered() && !this.getBooleanAnswer('ConsentInformBusiness');
  }
}
